import React, { useEffect, useRef } from 'react'

// Where a picked coordinate actually is.
//
// Two coordinates four decimals apart read the same in a table cell and can be a car park and the
// middle of a lake, so the survivor's location gets a map. The result is the red pin; every
// course in the group that has a location of its own is a small dot labelled with its id, so a
// pick that lands far from the rest is visible at a glance.
//
// Plain `google.maps`, not google-map-react. The API is already on the page (public/index.html),
// and both of google-map-react's ways of drawing a marker failed here: children are only
// re-laid-out when the map moves, so the pin stayed where the first value put it, and
// `onGoogleApiLoaded` never fired for a map mounted inside the already-open editor.

/** Google Maps with a pin on the point, for reviewing it at full size. */
export const googleMapsUrl = ({ lat, lng }) =>
  `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`

const mapsApi = () => window.google?.maps

export default function LocationPreview({ value, candidates = [] }) {
  const container = useRef(null)
  const map = useRef(null)
  const markers = useRef([])

  // value and candidates are rebuilt on every render; this is what they say.
  const key = JSON.stringify([value, candidates])

  useEffect(() => {
    const maps = mapsApi()
    if (!maps || !value || !container.current) return

    if (!map.current) {
      map.current = new maps.Map(container.current, {
        center: value,
        zoom: 15,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: 'cooperative',
      })
    }

    // Rebuilt rather than diffed: two or three markers do not need a bookkeeping layer.
    markers.current.forEach((marker) => marker.setMap(null))
    markers.current = [
      ...candidates.map(
        (candidate) =>
          new maps.Marker({
            map: map.current,
            position: candidate.location,
            title: `#${candidate.courseId}`,
            label: { text: `#${candidate.courseId}`, fontSize: '10px', color: '#212529' },
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 11,
              fillColor: '#fff',
              fillOpacity: 0.9,
              strokeColor: '#6c757d',
              strokeWeight: 1,
            },
          })
      ),
      new maps.Marker({ map: map.current, position: value, title: 'Survivor', zIndex: 10 }),
    ]

    // Framed on every point, not centred on the pick: the question is usually "which of these is
    // right", and a candidate just off the edge answers nothing. Capped, so two courses a few
    // metres apart do not zoom in to a single rooftop.
    const points = [value, ...candidates.map((candidate) => candidate.location)]
    const bounds = new maps.LatLngBounds()
    points.forEach((point) => bounds.extend(point))
    if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
      map.current.setCenter(value)
      map.current.setZoom(15)
    } else {
      map.current.fitBounds(bounds, 32)
      maps.event.addListenerOnce(map.current, 'idle', () => {
        if (map.current && map.current.getZoom() > 17) map.current.setZoom(17)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  useEffect(
    () => () => {
      markers.current.forEach((marker) => marker.setMap(null))
      map.current = null
    },
    []
  )

  if (!value) return null

  return (
    <div className="curation-location-preview mt-1">
      {mapsApi() && <div ref={container} className="curation-location-preview-map" />}
      <a href={googleMapsUrl(value)} target="_blank" rel="noreferrer" className="small">
        <i className="bi bi-box-arrow-up-right me-1" />
        Open in Google Maps
      </a>
    </div>
  )
}
